import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, Mail, Ticket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/useAuth'
import { ticketingService } from '../services/ticketing.service'
import { formatPrice } from '../components/TicketTypeSelector'

export function TicketSuccessPage() {
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('orderId')
  const receiptToken = searchParams.get('receiptToken')
  const { user, loading: authLoading } = useAuth()

  const [order, setOrder] = useState<Awaited<
    ReturnType<typeof ticketingService.getOrderReceipt>
  > | null>(null)
  const [orderLoading, setOrderLoading] = useState(true)

  useEffect(() => {
    if (!orderId || !receiptToken) {
      setOrder(null)
      setOrderLoading(false)
      return
    }
    ticketingService
      .getOrderReceipt(orderId, receiptToken)
      .then(setOrder)
      .catch(() => setOrder(null))
      .finally(() => setOrderLoading(false))
  }, [orderId, receiptToken])

  return (
    <div className="container mx-auto max-w-lg px-4 py-16 text-center space-y-8">
      <div className="space-y-3">
        <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
        <h1 className="text-3xl font-bold">Your tickets are confirmed!</h1>
        <p className="text-muted-foreground">
          Thank you for your purchase. Check your email for your PDF tickets.
        </p>
      </div>

      <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        <Mail className="h-5 w-5 shrink-0" />
        <span>PDF tickets with QR codes have been sent to your email address.</span>
      </div>

      {orderLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4 mx-auto" />
        </div>
      ) : order ? (
        <div className="rounded-lg border bg-card p-4 text-left space-y-3">
          <h2 className="font-semibold flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            Order Summary
          </h2>
          <div className="space-y-1.5 text-sm">
            <p className="font-medium">{order.eventTitle}</p>
            {order.lineItems.map((item, i) => (
              <div key={i} className="flex justify-between text-muted-foreground">
                <span>
                  {item.ticketTypeName} × {item.quantity}
                </span>
                <span>{formatPrice(item.unitPriceCents * item.quantity)}</span>
              </div>
            ))}
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total</span>
              <span>{formatPrice(order.totalCents)}</span>
            </div>
          </div>
        </div>
      ) : null}

      {!authLoading && (
        <div className="space-y-3">
          {user ? (
            <Button asChild className="w-full">
              <Link to="/my-tickets">View My Tickets</Link>
            </Button>
          ) : (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
              <p className="text-sm font-medium">Save your tickets — create an account</p>
              <p className="text-xs text-muted-foreground">
                Create a free account to easily access all your tickets in one place.
              </p>
              <Button asChild className="w-full">
                <Link to="/register">Create Account</Link>
              </Button>
            </div>
          )}
          <Button asChild variant="outline" className="w-full">
            <Link to="/events">Browse More Events</Link>
          </Button>
        </div>
      )}
    </div>
  )
}
